import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Newspaper, RefreshCw, Eye, X, Check, Ban, AlertTriangle, Shield } from 'lucide-react';

import { fetchModerationQueueThunk, decideModerationThunk } from '../../../redux/news/newsActionThunks';
import { selectModerationQueue } from '../../../redux/news/newsSlice';
import CredibilityChip from '../../../components/Credibility/CredibilityChip';

// Mirrors AdminEvidenceModerationComponent.jsx's own pattern (itself mirroring
// AdminModerationComponent.jsx) -- fetch-on-mount, theme classes, error
// banner, table + detail modal, .unwrap()+try/catch on every mutating
// action so a failure surfaces a real error instead of silently closing
// the modal (see that component's own comment for why this matters).
const AdminNewsModerationComponent = ({ darkMode = true }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [detailArticleId, setDetailArticleId] = useState(null);
  const [reason, setReason] = useState('');

  const queue = useSelector(selectModerationQueue);
  const loading = useSelector((state) => state.news?.moderationLoading || false);
  const error = useSelector((state) => state.news?.moderationError || state.news?.decisionError);

  const themeClasses = {
    cardBg: darkMode ? 'bg-gray-800' : 'bg-white',
    text: darkMode ? 'text-white' : 'text-gray-900',
    textSecondary: darkMode ? 'text-gray-300' : 'text-gray-600',
    textMuted: darkMode ? 'text-gray-400' : 'text-gray-500',
    border: darkMode ? 'border-gray-700' : 'border-gray-200',
    buttonSecondary: darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700',
    tableHeaderBg: darkMode ? 'bg-gray-700' : 'bg-gray-50',
    tableRowHover: darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50',
  };

  const fetchQueue = useCallback((page) => {
    const skip = (page - 1) * pageSize;
    dispatch(fetchModerationQueueThunk({ skip, limit: pageSize }));
  }, [dispatch, pageSize]);

  useEffect(() => {
    fetchQueue(currentPage);
  }, [currentPage, fetchQueue]);

  const openDetail = (articleId) => {
    setDetailArticleId(articleId);
    setReason('');
  };

  const handleDecision = async (articleId, decision) => {
    try {
      await dispatch(decideModerationThunk({ articleId, decision, reason: reason || null })).unwrap();
      setDetailArticleId(null);
    } catch {
      // error is in state.news.decisionError; banner renders it, modal stays
      // open so the moderator can see and retry -- same reasoning as the
      // evidence-moderation component's own handlers.
    }
  };

  const detailArticle = queue.find((a) => a.id === detailArticleId);

  return (
    <div className="space-y-6">
      {error && (
        <div className={`${darkMode ? 'bg-red-900/50 border-red-800' : 'bg-red-50 border-red-200'} border rounded-lg p-4`}>
          <div className="flex items-center">
            <AlertTriangle className={`h-5 w-5 ${darkMode ? 'text-red-400' : 'text-red-600'} mr-2`} />
            <span className={`${darkMode ? 'text-red-300' : 'text-red-800'} font-medium`}>{error}</span>
          </div>
        </div>
      )}

      <div className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} shadow-sm`}>
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h2 className={`text-xl font-semibold ${themeClasses.text} flex items-center`}>
              <Newspaper className="h-6 w-6 mr-2" />
              {t('admin.newsModeration.title')}
            </h2>
            <span className={`px-2 py-1 text-xs rounded-full ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>
              {t('admin.newsModeration.onThisPage', { count: queue.length })}
            </span>
          </div>
          <button
            onClick={() => fetchQueue(currentPage)}
            disabled={loading}
            className={`flex items-center px-4 py-2 ${themeClasses.buttonSecondary} rounded-lg transition-colors disabled:opacity-50`}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {t('admin.newsModeration.refresh')}
          </button>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin mr-2 text-blue-600" />
              <span className={themeClasses.text}>{t('admin.newsModeration.loadingArticles')}</span>
            </div>
          ) : queue.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className={themeClasses.tableHeaderBg}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>{t('admin.newsModeration.table.article')}</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>{t('admin.newsModeration.table.author')}</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>{t('admin.newsModeration.table.aiScreen')}</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>{t('admin.newsModeration.table.actions')}</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {queue.map((a) => (
                  <tr key={a.id} className={themeClasses.tableRowHover}>
                    <td className="px-6 py-4">
                      <div className={`text-sm font-medium ${themeClasses.text}`}>#{a.id}: {a.title}</div>
                      <div className={`text-xs ${themeClasses.textMuted}`}>{a.tickers?.join(', ')}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className={`text-sm ${themeClasses.textMuted} flex items-center gap-1.5 flex-wrap`}>
                        <Shield className="h-3 w-3" /> {a.author_username || t('admin.newsModeration.userFallback', { id: a.author_id })}
                        <CredibilityChip cred={a.author_credibility} size="xs" showAccuracy={false} />
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {a.ai_detection_flag === null || a.ai_detection_flag === undefined ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                          {t('admin.newsModeration.notYetEvaluated')}
                        </span>
                      ) : a.ai_detection_flag ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                          {t('admin.newsModeration.flagged', { score: a.ai_detection_score?.toFixed(2) })}
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                          {t('admin.newsModeration.clean')}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => openDetail(a.id)}
                        className="p-1 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                        title={t('admin.newsModeration.viewFullArticle')}
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className={`text-center py-12 ${themeClasses.textMuted}`}>
              <Newspaper className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">{t('admin.newsModeration.nothingPending')}</p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end space-x-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
            className={`px-3 py-2 ${themeClasses.buttonSecondary} rounded-lg transition-colors disabled:opacity-50`}
          >
            {t('admin.newsModeration.previous')}
          </button>
          <span className={`px-3 py-2 text-sm ${themeClasses.text}`}>{t('admin.newsModeration.page', { page: currentPage })}</span>
          <button
            onClick={() => setCurrentPage((p) => p + 1)}
            disabled={queue.length < pageSize}
            className={`px-3 py-2 ${themeClasses.buttonSecondary} rounded-lg transition-colors disabled:opacity-50`}
          >
            {t('admin.newsModeration.next')}
          </button>
        </div>
      </div>

      {detailArticleId && detailArticle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} p-6 w-full max-w-2xl mx-4 shadow-xl max-h-[85vh] overflow-y-auto`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className={`text-lg font-semibold ${themeClasses.text}`}>{t('admin.newsModeration.articleNumber', { id: detailArticle.id })}</h3>
              <button onClick={() => setDetailArticleId(null)} className={`${themeClasses.textMuted} hover:${themeClasses.text}`}>
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3 mb-6">
              <div className={`text-base font-medium ${themeClasses.text}`}>{detailArticle.title}</div>
              <div className={`text-sm ${themeClasses.textMuted} flex items-center gap-1.5 flex-wrap`}>
                <Shield className="h-3 w-3" /> {detailArticle.author_username || t('admin.newsModeration.userFallback', { id: detailArticle.author_id })}
                <CredibilityChip cred={detailArticle.author_credibility} size="xs" showAccuracy={false} />
              </div>
              <p className={`text-sm ${themeClasses.textSecondary} whitespace-pre-wrap max-h-64 overflow-y-auto p-3 rounded-lg border ${themeClasses.border} ${darkMode ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
                {detailArticle.body_content}
              </p>
            </div>

            <div className="mb-4">
              <label className={`block text-sm font-semibold ${themeClasses.text} mb-2`}>{t('admin.newsModeration.decisionReasonLabel')}</label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('admin.newsModeration.decisionReasonPlaceholder')}
                className={`w-full px-3 py-2 rounded-lg border ${themeClasses.border} ${darkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'} text-sm`}
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => handleDecision(detailArticle.id, 'approve')}
                className="flex items-center px-3 py-1.5 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
              >
                <Check className="h-3 w-3 mr-1" /> {t('admin.newsModeration.approveAndPublish')}
              </button>
              <button
                onClick={() => handleDecision(detailArticle.id, 'reject')}
                className="flex items-center px-3 py-1.5 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
              >
                <Ban className="h-3 w-3 mr-1" /> {t('admin.newsModeration.reject')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNewsModerationComponent;
